import type { ConversationAttributes } from '@/server/models'

import ConversationUserQ from '@/queries/ConversationUserQ'
import { SQL, type SQLFragment } from '@/client/query'
import type { ConversationType } from '@/client/services/conversation'

import {
  type AssociationDefinitions,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryOrderToSQL,
} from './common'
import SeriesQ from './SeriesQ'

const ConversationQ = {
  ...makeQueryModelV2<ConversationAttributes>('conversation')([
    'id',
    'type',
    'key',
    'series_id',
    'session_id',
    'external_uuid',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Series: {
      query: SeriesQ,
      foreignKey: SeriesQ.id,
      sourceKey: ConversationQ.series_id,
    },
    ConversationUser: {
      query: ConversationUserQ,
      foreignKey: ConversationUserQ.conversation_id,
      sourceKey: ConversationQ.id,
    },
  }),

  hasType: (type: ConversationType): SQLFragment<boolean> => SQL`${ConversationQ.type} = ${type}`,

  where: (
    {
      id,
      type,
      key,
      seriesId,
      sessionId,
      include = [],
      order = null,
      paranoid = true,
    }: {
      id?: number | SQLFragment<number> | null
      type?: ConversationType
      key?: string | null
      seriesId?: number | SQLFragment<number> | null
      sessionId?: number | SQLFragment<number> | null
      include?: IncludeAssociations
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<ConversationAttributes> => SQL`
        FROM ${ConversationQ}
        ${includeAssociationsToSql(ConversationQ, include)}
        WHERE
            ${id != null ? SQL`${ConversationQ.id} = ${id} AND` : SQL``}
            ${type != null ? SQL`${ConversationQ.type} = ${type} AND` : SQL``}
            ${key != null ? SQL`${ConversationQ.key} = ${key} AND` : SQL``}
            ${seriesId != null ? SQL`${ConversationQ.series_id} = ${seriesId} AND` : SQL``}
            ${sessionId != null ? SQL`${ConversationQ.session_id} = ${sessionId} AND` : SQL``}
            ${paranoid ? SQL`${ConversationQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryOrderToSQL(order)}
        `,
}

export default ConversationQ
